import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  ISidebarThreadItem,
  ISidebarThreadItems,
  IThread,
  IThreadAgent,
} from '@/api/modules/threads/types'
import { ThreadsApi } from '@/api/modules/threads/api'

const extractQueryString = (url: string | null | undefined): string | undefined => {
  if (!url) return undefined
  try {
    const urlObj = new URL(url)
    return urlObj.search
  } catch {
    return url
  }
}

export const useThreadsStore = defineStore('threadsStore', () => {
  const currentThread = ref<ISidebarThreadItem>()
  const todayData = ref<ISidebarThreadItems>([])
  const yesterdayData = ref<ISidebarThreadItems>([])
  const weekData = ref<ISidebarThreadItems>([])

  const hasNextTodayData = ref<string>()
  const hasNextYesterdayData = ref<string>()
  const hasNextWeekData = ref<string>()

  const loading = ref(false)

  const thread = ref<IThread>()
  const threadAgents = ref<IThreadAgent[]>([])

  const setCurrentThread = (newSidebarThreadItem: ISidebarThreadItem, isPersist = false) => {
    const inToodayIndex = (todayData.value || []).findIndex(
      (i) => i.thread_id === newSidebarThreadItem.thread_id,
    )

    if (inToodayIndex !== -1) {
      todayData.value[inToodayIndex] = newSidebarThreadItem

      return
    }

    const inYesterdayIndex = (yesterdayData.value || []).findIndex(
      (i) => i.thread_id === newSidebarThreadItem.thread_id,
    )

    if (inYesterdayIndex !== -1) {
      yesterdayData.value[inYesterdayIndex] = newSidebarThreadItem

      return
    }

    const inWeekDataIndex = (weekData.value || []).findIndex(
      (i) => i.thread_id === newSidebarThreadItem.thread_id,
    )

    if (inWeekDataIndex !== -1) {
      weekData.value[inWeekDataIndex] = newSidebarThreadItem

      return
    }

    todayData.value = [newSidebarThreadItem, ...todayData.value]
  }

  const getThreadInfo = async (threadId: string) => {
    try {
      const { data } = await ThreadsApi.getThreadInfo(threadId)
      thread.value = data
    } catch (e) {
      console.log(threadId)
    }
  }

  const resetThread = () => {
    thread.value = undefined
  }

  const allThreads = computed(() => [...todayData.value, ...yesterdayData.value, ...weekData.value])

  const handleGetThreads = async () => {
    try {
      loading.value = true
      const [todayRes, yesterdayRes, weekRes] = await Promise.all([
        ThreadsApi.getSidebarThreads(undefined, 5, 'today'),
        ThreadsApi.getSidebarThreads(undefined, 5, 'yesterday'),
        ThreadsApi.getSidebarThreads(undefined, 5, 'previous_7_days'),
      ])

      todayData.value = todayRes.data.results || []
      hasNextTodayData.value = extractQueryString(todayRes.data.next)

      yesterdayData.value = yesterdayRes.data.results || []
      hasNextYesterdayData.value = extractQueryString(yesterdayRes.data.next)

      weekData.value = weekRes.data.results || []
      hasNextWeekData.value = extractQueryString(weekRes.data.next)
    } catch (e) {
      console.error('Error loading threads:', e)
    } finally {
      loading.value = false
    }
  }

  const getNextTodayThreads = async (type: string) => {
    try {
      if (!hasNextTodayData.value) return

      const todayRes = await ThreadsApi.getSidebarThreadsNext(hasNextTodayData.value)
      todayData.value = [...todayData.value, ...(todayRes.data.results || [])]
      hasNextTodayData.value = extractQueryString(todayRes.data.next)
    } catch (e) {
      console.error('Error loading threads:', e)
    }
  }

  const getNextYesterdayDataThreads = async (type: string) => {
    try {
      if (!hasNextYesterdayData.value) return

      const yesterdayRes = await ThreadsApi.getSidebarThreadsNext(hasNextYesterdayData.value)
      yesterdayData.value = [...yesterdayData.value, ...(yesterdayRes.data.results || [])]
      hasNextYesterdayData.value = extractQueryString(yesterdayRes.data.next)
    } catch (e) {
      console.error('Error loading threads:', e)
    }
  }

  const getNextWeekDataThreads = async (type: string) => {
    try {
      if (!hasNextWeekData.value) return

      const weekRes = await ThreadsApi.getSidebarThreadsNext(hasNextWeekData.value)
      weekData.value = [...weekData.value, ...(weekRes.data.results || [])]
      hasNextWeekData.value = extractQueryString(weekRes.data.next)
    } catch (e) {
      console.error('Error loading threads:', e)
    }
  }

  const getThreadAgents = async (threadId: string) => {
    try {
      const { data } = await ThreadsApi.getThreadAgents(threadId)
      threadAgents.value = data
      return data
    } catch (e) {
      console.error('Error loading thread agents:', e)
      throw e
    }
  }

  const switchThreadAgent = async (threadId: string, agentId: string) => {
    try {
      const { data } = await ThreadsApi.switchThreadAgent(threadId, { agent_id: agentId })
      thread.value = data
    } catch (e) {
      console.error('Error switching thread agent:', e)
      throw e
    }
  }

  return {
    currentThread,
    todayItems: todayData,
    yesterdayItems: yesterdayData,
    weekItems: weekData,
    allThreads,
    thread,
    threadAgents,
    loading,

    hasNextTodayData,
    hasNextYesterdayData,
    hasNextWeekData,

    getNextTodayThreads,
    getNextYesterdayDataThreads,
    getNextWeekDataThreads,
    getThreadAgents,
    switchThreadAgent,

    handleGetThreads,
    resetThread,
    setCurrentThread,
    getThreadInfo,
  }
})
